/**
 * 계측 사진 오프라인 저장 유틸리티
 * Feature: #411 (계측 오프라인 동기화 - Phase 4)
 *
 * 오프라인 시 계측 사진을 로컬에 저장하고 syncQueue에 추가
 * 온라인 복귀 시 syncPhotos()에서 자동 업로드 후 updateMeasure로 URL 반영
 */

import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { createLogger } from "@ict/shared";
import { saveLocalPhoto } from "./db/photos";
import { addToSyncQueue } from "./db/changes";
import {
  type UploadResult,
  fileToDataUrl,
  getBase64FileSize,
  resizeImageForThumbnail,
} from "./upload";

const logger = createLogger({ module: "UploadMeasurePhoto" });

interface MeasurePhotoParams {
  projectId: string;
  docKey: string;
  machineId: string;
  measureType: string;
  measureIndex: number;
  slot: "check" | "result";
}

/**
 * 계측 사진 오프라인 저장
 * 기존 savePhotoOffline 패턴을 계측 사진 전용으로 구현
 *
 * 흐름:
 * 1. 이미지 → base64 변환
 * 2. 네이티브: Filesystem에 저장, 웹: IndexedDB에 base64 저장
 * 3. LocalPhoto 메타데이터 저장 (measureType, measureIndex, slot 포함)
 * 4. syncQueue에 photo 항목 추가 (uploadType: "measurePhoto")
 */
export async function uploadMeasurePhoto(
  file: File,
  params: MeasurePhotoParams
): Promise<UploadResult> {
  const { projectId, docKey, machineId, measureType, measureIndex, slot } = params;
  const timestamp = Date.now();
  const filename = `measure_${measureType}_${measureIndex}_${slot}_${timestamp}.jpg`;

  try {
    const dataUrl = await fileToDataUrl(file);
    const thumbnail = await resizeImageForThumbnail(dataUrl);
    const machineCompositeId = `${projectId}_${machineId}`;
    const fileSize = getBase64FileSize(dataUrl);

    let localPath = "";
    let localUrl: string;

    if (Capacitor.isNativePlatform()) {
      const base64Data = dataUrl.split(",")[1] || dataUrl;
      const filePath = `photos/${filename}`;

      const writeResult = await Filesystem.writeFile({
        path: filePath,
        data: base64Data,
        directory: Directory.Data,
        recursive: true,
      });

      localPath = writeResult.uri;
      localUrl = Capacitor.convertFileSrc(writeResult.uri);
    } else {
      // 웹 환경: base64 데이터를 IndexedDB에 저장
      localPath = `indexeddb://${filename}`;
      localUrl = thumbnail;
    }

    // IndexedDB에 메타데이터 저장 (계측 사진 전용 필드 포함)
    const photoId = await saveLocalPhoto({
      machineId: machineCompositeId,
      projectId,
      localPath,
      base64Thumbnail: thumbnail,
      base64Data: Capacitor.isNativePlatform() ? undefined : dataUrl,
      fileName: filename,
      mimeType: file.type || "image/jpeg",
      fileSize,
      synced: false,
      measureType,
      measureIndex,
      slot,
    });

    // 동기화 큐에 추가
    await addToSyncQueue({
      type: "photo",
      entityId: photoId,
      operation: "create",
      projectId,
      docKey,
      machineId,
      payload: {
        photoId,
        localPath,
        uploadType: "measurePhoto",
        measureType,
        measureIndex,
        slot,
      },
    });

    logger.info("계측 사진 오프라인 저장 완료", {
      photoId,
      measureType,
      measureIndex,
      slot,
    });

    return {
      success: true,
      url: localUrl,
      isLocal: true,
      localPhotoId: photoId,
      errorCode: "OFFLINE_SAVED",
    };
  } catch (error) {
    logger.error("계측 사진 오프라인 저장 실패", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "오프라인 저장 실패",
      errorCode: "UPLOAD_FAILED",
    };
  }
}
